import { Fragment } from "react";
import { FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { displayName, groupLinesByMember, lineDocumentName } from "../../lib/linesDisplay";
import { docBlobName } from "@/components/ledger/V4Edits";

// ---------------------------------------------------------------------------
// LinesTable — flat, cross-position ledger feed (#63).
//
// Fed by GET /api/ledger/lines: one row per DOCUMENT'S OWN stated cashflow
// fact (row_class='event' AND type IN {capital_call_event_amount,
// distribution_event_amount} — the backend predicate already excludes
// cumulative/NAV restatements and gate-rejected rows). Founder contract
// (2026-07-10): "the ops lines view is for each doc to tell what happened
// cashflow wise in that doc. unattached to a larger fund." So this component
// renders each row's `type` + `amount` VERBATIM — no differencing against
// other docs, no cumulative-position fold, no inferred movement. There is no
// contributions drilldown anymore: the row IS the fact, nothing to expand.
//
// Each row already carries its own currency and its own discarded/excluded
// tags — this component NEVER sums across rows, so it never risks summing
// across currencies.
//
// #63 follow-up (2026-07-21): Fund/Member cells now render through the
// display-name map (real title, UPPERCASE — see lib/linesDisplay.js), a new
// Group column shows which client group the row's investor resolves into
// (same fold semantics the group FILTER uses, so it's always in parity), and
// the doc icon can either open the in-page DocumentDetailDialog popup
// (onSelectDoc prop) or fall back to the legacy SAS `<a>` link. Filtering
// itself is untouched — it still keys on the RAW line values.
// ---------------------------------------------------------------------------

function safeCurrencyCode(currency) {
  const normalized = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "USD";
}

function formatCurrency(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: safeCurrencyCode(currency),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return String(dateStr).slice(0, 10);
}

function truncate(str, maxLen = 40) {
  if (!str) return "—";
  return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
}

const MINUS = "−"; // U+2212 true minus sign

// type -> badge label/color + cashflow direction. Only the two backend flow
// types get a real direction; anything else falls back to a neutral badge
// showing the RAW type string — never invented/guessed.
const TYPE_META = {
  capital_call_event_amount: {
    label: "Call",
    sign: -1,
    badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
    amountClass: "text-rose-600",
  },
  distribution_event_amount: {
    label: "Distribution",
    sign: 1,
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amountClass: "text-emerald-600",
  },
};

function movementFor(line) {
  const meta = TYPE_META[line.type];
  if (meta) return meta;
  return {
    label: line.type || "Unknown",
    sign: 0,
    badgeClass: "bg-gray-100 text-gray-500 border-gray-200",
    amountClass: "text-gray-600",
  };
}

function formatSigned(amount, currency, sign) {
  if (amount == null) return null;
  const magnitude = Math.abs(amount);
  if (sign === 0) return formatCurrency(magnitude, currency);
  return (sign < 0 ? MINUS : "+") + formatCurrency(magnitude, currency);
}

// Total column count (Portal/Fund/Member/Group/Date/Movement/Amount/Document/Tags/PDF) —
// used as the colSpan for the full-width member-section header row.
const COLUMN_COUNT = 10;

function LineRow({ line, fundMap, memberMap, resolveGroup, onSelectDoc }) {
  const mv = movementFor(line);
  const signed = formatSigned(line.amount, line.currency, mv.sign);
  // Cell text = the patterned "Group_Member_Type_Date" doc name; the tooltip stays the
  // SOURCE filename (original_filename, else the source_doc_id's last path segment) so
  // a client can always confirm which uploaded file a row's document is.
  const sourceDocLabel = line.original_filename || line.source_doc_id?.split("/").pop() || "Unknown";
  const docName = line.doc_type || lineDocumentName(line, { memberMap, resolveGroup }); // v4 lines carry their doc type
  const fundDisplay = displayName(line.fund, fundMap);
  const memberDisplay = displayName(line.investor, memberMap);
  const groupLabel = resolveGroup(line.investor) || "—";

  return (
    <tr
      className="border-t border-gray-100 hover:bg-gray-50"
      data-testid="line-row"
      data-discarded={line.discarded ? "true" : undefined}
      data-excluded={line.excluded ? "true" : undefined}
    >
      <td className="px-2 py-2 text-gray-600 whitespace-nowrap max-w-[120px] truncate" title={line.portal}>
        {line.portal || "—"}
      </td>
      <td className="px-2 py-2 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={fundDisplay}>
        {fundDisplay || "—"}
      </td>
      <td className="px-2 py-2 text-gray-600 whitespace-nowrap max-w-[160px] truncate" title={memberDisplay}>
        {memberDisplay || "—"}
      </td>
      <td className="px-2 py-2 text-gray-500 whitespace-nowrap max-w-[140px] truncate" title={groupLabel}>
        {groupLabel}
      </td>
      <td className="px-2 py-2 font-mono text-gray-600 whitespace-nowrap">{formatDate(line.event_date)}</td>
      <td className="px-2 py-2 whitespace-nowrap">
        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${mv.badgeClass}`}>
          {mv.label}
        </span>
      </td>
      {/* Amount — this document's OWN stated fact, ALWAYS paired with this row's own currency.
          Never a running total, never differenced against another doc. */}
      <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
        {signed == null ? (
          <span className="text-gray-300">—</span>
        ) : (
          <>
            <span className={mv.amountClass}>{signed}</span>{" "}
            <span className="text-[10px] text-gray-400">{safeCurrencyCode(line.currency)}</span>
          </>
        )}
      </td>
      <td className="px-2 py-2 max-w-[200px]">
        <span className="truncate text-gray-400 block" title={sourceDocLabel}>
          {truncate(docName, 40)}
        </span>
      </td>
      {/* Tags — discarded/excluded render WITH the row, never hide it (founder decision: truthful UI). */}
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="flex items-center gap-1">
          {line.discarded && (
            <Badge
              variant="outline"
              className="text-[9px] border-red-300 text-red-700 bg-red-50"
              data-testid="line-tag-discarded"
            >
              discarded
            </Badge>
          )}
          {line.excluded && (
            <Badge
              variant="outline"
              className="text-[9px] border-amber-300 text-amber-700 bg-amber-50"
              data-testid="line-tag-excluded"
            >
              excluded
            </Badge>
          )}
        </div>
      </td>
      <td className="px-2 py-2 text-right">
        {onSelectDoc ? (
          docBlobName(line) && (
            <button
              type="button"
              data-testid="line-doc-open"
              onClick={() => onSelectDoc(line)}
              className="inline-flex p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
              title="View source document"
              aria-label="View source document"
            >
              <FileText className="w-3.5 h-3.5" />
            </button>
          )
        ) : (
          line.download_url && (
            <a
              href={line.download_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
              title="View source PDF"
              aria-label="View source PDF"
            >
              <FileText className="w-3.5 h-3.5" />
            </a>
          )
        )}
      </td>
    </tr>
  );
}

function lineKey(line, fallbackIndex) {
  return line.event_id || `${line.source_doc_id || "doc"}|${line.stream_id || ""}|${line.event_date}|${fallbackIndex}`;
}

function ColumnHeaders() {
  return (
    <tr>
      <th className="px-2 py-2 text-left font-medium">Portal</th>
      <th className="px-2 py-2 text-left font-medium">Fund</th>
      <th className="px-2 py-2 text-left font-medium">Member</th>
      <th className="px-2 py-2 text-left font-medium">Group</th>
      <th className="px-2 py-2 text-left font-medium w-24">Date</th>
      <th className="px-2 py-2 text-left font-medium w-28">Movement</th>
      <th className="px-2 py-2 text-right font-medium w-36">Amount</th>
      <th className="px-2 py-2 text-left font-medium">Document</th>
      <th className="px-2 py-2 text-left font-medium">Tags</th>
      <th className="px-2 py-2 text-right font-medium w-10" aria-label="PDF" />
    </tr>
  );
}

/**
 * Props:
 *   lines {array} — from GET /api/ledger/lines, already sorted latest event_date
 *                    first by the backend. This component renders as-served —
 *                    it does not resort.
 *   groupByMember {bool} — when true, rows are grouped into per-member sections
 *                    (via lib/linesDisplay groupLinesByMember) with a full-width
 *                    header row per section. Default false = flat table, as today.
 *   fundMap {object} — buildFundDisplayMap() output; keys the Fund cell's display
 *                    string. Defaults to {} (falls back to the raw slug, uppercased).
 *   memberMap {object} — buildMemberDisplayMap() output; same idea for Member.
 *   resolveGroup {function} — buildGroupResolver() output, (investor) => group name
 *                    or "". Defaults to a function that always returns "".
 *   onSelectDoc {function|null} — when provided, the doc icon becomes a <button>
 *                    calling onSelectDoc(line) to open the in-page document popup
 *                    instead of navigating to the SAS URL directly.
 */
export default function LinesTable({
  lines = [],
  groupByMember = false,
  fundMap = {},
  memberMap = {},
  resolveGroup = () => "",
  onSelectDoc = null,
}) {
  if (lines.length === 0) {
    return <div className="text-xs text-gray-400 py-3 px-3">No ledger lines recorded.</div>;
  }

  const sections = groupByMember ? groupLinesByMember(lines, memberMap) : null;

  return (
    <div className="overflow-x-auto overflow-y-auto">
      <table className="w-full text-xs" data-testid="lines-table">
        <thead className="bg-gray-50 text-gray-500 uppercase sticky top-0 z-10">
          <ColumnHeaders />
        </thead>
        <tbody>
          {sections
            ? sections.map((section) => {
                const sectionGroup = section.lines.length > 0 ? resolveGroup(section.lines[0].investor) : "";
                return (
                  <Fragment key={section.key || "—"}>
                    <tr data-testid="lines-member-section" className="bg-gray-50/70 border-t border-gray-200">
                      <td colSpan={COLUMN_COUNT} className="px-2 py-1.5 text-[11px] font-semibold text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>{section.label}</span>
                          {sectionGroup && (
                            <Badge
                              variant="outline"
                              className="text-[9px] border-indigo-200 text-indigo-700 bg-indigo-50 font-normal"
                            >
                              {sectionGroup}
                            </Badge>
                          )}
                          <span className="text-gray-400 font-normal">
                            {section.lines.length} line{section.lines.length === 1 ? "" : "s"}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {section.lines.map((line, i) => (
                      <LineRow
                        key={lineKey(line, `${section.key}-${i}`)}
                        line={line}
                        fundMap={fundMap}
                        memberMap={memberMap}
                        resolveGroup={resolveGroup}
                        onSelectDoc={onSelectDoc}
                      />
                    ))}
                  </Fragment>
                );
              })
            : lines.map((line, i) => (
                <LineRow
                  key={lineKey(line, i)}
                  line={line}
                  fundMap={fundMap}
                  memberMap={memberMap}
                  resolveGroup={resolveGroup}
                  onSelectDoc={onSelectDoc}
                />
              ))}
        </tbody>
      </table>
    </div>
  );
}
